import {
  createFormSubmitHandler,
  NvForm,
  NvModal,
} from '@simwood/nv-react-components';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { smsMessageReceived } from '../../reducers/sms';
import { sendSmsMessage } from '../../services/smsService';
import { StoreDispatch, StoreState } from '../../store';
import { SmsSend } from '../../types/Sms';
import CallerIdPicker from '../CallerIdPicker';

interface Props {
  fullScreen?: boolean;
  onClose: () => void;
}

type FieldValues = Omit<SmsSend, 'from'> & { defaultCallerId: string | null };

const baseEntry: FieldValues = {
  to: '',
  defaultCallerId: null,
  body: '',
};

const NewSmsModal: React.FC<Props> = ({ onClose }) => {
  const customerId = useSelector(
    (state: StoreState) => state.client.activeCustomer?.id,
  );
  const dispatch = useDispatch<StoreDispatch>();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    setError,
  } = useForm<FieldValues>({
    mode: 'onBlur',
    defaultValues: baseEntry,
  });

  const [isSending, setIsSending] = useState(false);

  const outgoingCallerIds = useSelector(
    (state: StoreState) => state.client.outgoingCallerIds || [],
  );

  const send = useCallback(
    async (values: FieldValues) => {
      if (!customerId) {
        return;
      }
      try {
        setIsSending(true);
        const callerId = outgoingCallerIds.find(
          (c) => c.id === values.defaultCallerId,
        );
        if (!callerId) {
          const errorMessage = 'Caller id not found';
          setError('defaultCallerId', { message: errorMessage });
          throw new Error(errorMessage);
        }
        const message = await sendSmsMessage(customerId, {
          ...values,
          from: callerId.number || '',
        });
        dispatch(smsMessageReceived(message));
      } finally {
        setIsSending(false);
      }
    },
    [customerId, dispatch, outgoingCallerIds, setError],
  );

  if (!customerId) {
    return null;
  }

  return (
    <NvModal.Form
      // TODO
      // size={fullScreen ? 'fullScreen' : 'standard'}
      title='New SMS'
      open={true}
      setOpen={(open) => {
        if (!open) {
          onClose();
        }
      }}
      disabled={isSending || !isDirty}
      closeText='Cancel'
      submitText='Send'
      closeOnSubmit
      onSubmit={createFormSubmitHandler(handleSubmit, send)}
    >
      <NvForm.Input
        name='to'
        labelText='Recipient'
        type='number'
        placeholder='Enter number here'
        required
        control={control}
      />
      <CallerIdPicker control={control} labelText='From' onlySmsCapable />
      <NvForm.TextArea
        labelText='Message'
        name='body'
        control={control}
        minRows={6}
        required
      />
    </NvModal.Form>
  );
};

export default NewSmsModal;
